import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import libFloat from "../libFloat";
import functions from "../functions";

/**
 * Gera um relatório PDF completo do Fechamento de Caixa.
 */
export function generateFechamentoPDF({ dataFechamento, dataItens, dataOutroCaixa }) {
	if (!dataFechamento) return;

	const doc = new jsPDF("p", "pt", "a4");
	const margin = 40;
	const pageWidth = doc.internal.pageSize.getWidth();
	const blueColor = [41, 128, 185]; // Padrão do sistema

	// --- FUNÇÕES AUXILIARES ---
	const formatCurrency = (val) => libFloat.strToCurrency(val || 0);
	const formatDate = (val) => functions.dateTimeToStrIso8601(val);

	// ==========================================
	// CABEÇALHO DO RELATÓRIO
	// ==========================================
	let y = 45;
	doc.setFontSize(22);
	doc.setFont("helvetica", "bold");
	doc.setTextColor(blueColor[0], blueColor[1], blueColor[2]);
	doc.text("FECHAMENTO DE CAIXA", margin, y);

	// Linha decorativa abaixo do título
	doc.setDrawColor(blueColor[0], blueColor[1], blueColor[2]);
	doc.setLineWidth(2);
	doc.line(margin, y + 8, margin + 80, y + 8);

	// Informações de Emissão (Direita)
	doc.setFontSize(9);
	doc.setFont("helvetica", "normal");
	doc.setTextColor(100);
	doc.text(`Emitido em: ${formatDate(new Date())}`, pageWidth - margin, y - 5, { align: "right" });
	doc.text(`Identificador: #${dataFechamento.id_sessao || "—"}`, pageWidth - margin, y + 10, { align: "right" });

	y += 45;

	// ==========================================
	// SEÇÃO 1: DADOS DA SESSÃO
	// ==========================================
	doc.setFontSize(12);
	doc.setFont("helvetica", "bold");
	doc.setTextColor(40);
	doc.text("1. DADOS DA SESSÃO", margin, y);
	y += 15;

	autoTable(doc, {
		startY: y,
		body: [
			["Número do Caixa:", dataFechamento.numero_caixa || "—", "ID Sessão:", `#${dataFechamento.id_sessao || "—"}`],
			["Operador Abertura:", dataFechamento.operador_abertura || "—", "Início:", formatDate(dataFechamento.data_abertura)],
			["Operador Fechamento:", dataFechamento.operador_fechamento || "—", "Fim:", formatDate(dataFechamento.data_fechamento)],
		],
		theme: "plain",
		styles: { fontSize: 9, cellPadding: 2 },
		columnStyles: {
			0: { fontStyle: "bold", textColor: 100, cellWidth: 100 },
			1: { fontStyle: "bold", textColor: 40, cellWidth: 150 },
			2: { fontStyle: "bold", textColor: 100, cellWidth: 100 },
			3: { fontStyle: "bold", textColor: 40 },
		},
		margin: { left: margin },
	});

	y = doc.lastAutoTable.finalY + 30;

	// ==========================================
	// SEÇÃO 2: MOVIMENTAÇÃO EM DINHEIRO
	// ==========================================
	doc.setFontSize(12);
	doc.setFont("helvetica", "bold");
	doc.text("2. MOVIMENTAÇÃO EM DINHEIRO", margin, y);
	y += 10;

	const fluxRows = [];
	if (dataFechamento.valor_abertura > 0) fluxRows.push(["+ Valor de Abertura", formatCurrency(dataFechamento.valor_abertura)]);
	if (dataFechamento.vendas_dinheiro > 0) fluxRows.push(["+ Vendas em dinheiro", formatCurrency(dataFechamento.vendas_dinheiro)]);
	if (dataFechamento.suprimento > 0) fluxRows.push(["+ Suprimento", formatCurrency(dataFechamento.suprimento)]);
	if (dataFechamento.sangria > 0) fluxRows.push(["- Sangria", formatCurrency(dataFechamento.sangria)]);
	if (dataFechamento.pagamentos > 0) fluxRows.push(["- Pagamentos / saídas", formatCurrency(dataFechamento.pagamentos)]);
	if (dataFechamento.entregas > 0) fluxRows.push(["- Entregas", formatCurrency(dataFechamento.entregas)]);
	
	// Linha de Total
	fluxRows.push([{ content: "TOTAL EM DINHEIRO NO CAIXA", styles: { fontStyle: "bold", fillColor: [245, 247, 250] } }, 
                  { content: formatCurrency(dataFechamento.valor_expected_dinheiro || dataFechamento.valor_esperado_dinheiro), styles: { fontStyle: "bold", fillColor: [245, 247, 250] } }]);

	autoTable(doc, {
		startY: y,
		head: [["Descrição do Fluxo", "Valor (R$)"]],
		body: fluxRows,
		theme: "grid",
		headStyles: { fillColor: blueColor, textColor: 255 },
		columnStyles: {
            0: { cellWidth: "auto" },
			1: { halign: "right", cellWidth: 100 },
		},
		styles: { fontSize: 9 },
		margin: { left: margin, right: margin },
	});

	y = doc.lastAutoTable.finalY + 30;

	// ==========================================
	// SEÇÃO 3: CONFERÊNCIA POR FORMA DE PAGAMENTO
	// ==========================================
	doc.setFontSize(12);
	doc.setFont("helvetica", "bold");
	doc.text("3. CONFERÊNCIA POR FORMA DE PAGAMENTO", margin, y);
	y += 10;

	if (dataItens && dataItens.length > 0) {
		autoTable(doc, {
			startY: y,
			head: [["Forma de Pagamento", "Sistema", "Conferido", "Diferença"]],
			body: dataItens.map((it) => [
				it.forma_pgto.toUpperCase(),
				formatCurrency(it.valor_sistema),
				formatCurrency(it.valor_conferido),
				{ content: formatCurrency(it.diferenca), styles: { textColor: it.diferenca < 0 ? [200, 0, 0] : [0, 150, 0], fontStyle: "bold" } },
			]),
			theme: "striped",
			headStyles: { fillColor: blueColor, textColor: 255 },
			columnStyles: {
				1: { halign: "right" },
				2: { halign: "right" },
				3: { halign: "right" },
			},
			styles: { fontSize: 9 },
			margin: { left: margin, right: margin },
		});
	} else {
		doc.setFontSize(10);
		doc.setFont("helvetica", "italic");
		doc.text("Nenhum item de conferência encontrado.", margin, y + 10);
	}

	y = (doc.lastAutoTable.finalY || y + 20) + 30;

	// ==========================================
	// SEÇÃO 4: CONFERÊNCIA OPERACIONAL (Diferença Final)
	// ==========================================
	doc.setFontSize(12);
	doc.setFont("helvetica", "bold");
	doc.text("4. CONFERÊNCIA OPERACIONAL", margin, y);
	y += 10;

	autoTable(doc, {
		startY: y,
		body: [
			["Total Entradas no Sistema:", formatCurrency(dataFechamento.total_sistema_conf)],
			["Total Informado pelo Operador:", formatCurrency(dataFechamento.total_conferido)],
			[{ content: "SALDO DE DIFERENÇA:", styles: { fontStyle: "bold" } }, 
             { content: formatCurrency(dataFechamento.total_diferenca), styles: { fontStyle: "bold", textColor: dataFechamento.total_diferenca < 0 ? [200, 0, 0] : [0, 150, 0] } }],
		],
		theme: "plain",
		styles: { fontSize: 10, cellPadding: 3 },
		columnStyles: {
			0: { cellWidth: 200 },
			1: { halign: "right", fontStyle: "bold" },
		},
		margin: { left: margin },
	});

    // Se houver outras movimentações
    if (dataOutroCaixa && dataOutroCaixa.length > 0) {
        y = doc.lastAutoTable.finalY + 30;
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("5. OUTRAS MOVIMENTAÇÕES", margin, y);
        y += 10;

        autoTable(doc, {
            startY: y,
            head: [["Descrição", "Valor"]],
            body: dataOutroCaixa.map(it => [it.descricao, formatCurrency(it.valor)]),
            theme: "grid",
            headStyles: { fillColor: [100, 100, 100] },
            columnStyles: { 1: { halign: "right", cellWidth: 100 } },
            styles: { fontSize: 9 },
            margin: { left: margin, right: margin },
        });
    }

	// ==========================================
	// RODAPÉ: ASSINATURAS
	// ==========================================
	const footerY = doc.internal.pageSize.getHeight() - 80;
	doc.setDrawColor(200);
	doc.setLineWidth(0.5);

	// Linha Operador
	doc.line(margin, footerY, margin + 200, footerY);
	doc.setFontSize(8);
	doc.text("Assinatura do Operador", margin + 100, footerY + 12, { align: "center" });

	// Linha Supervisor
	doc.line(pageWidth - margin - 200, footerY, pageWidth - margin, footerY);
	doc.text("Assinatura do Supervisor", pageWidth - margin - 100, footerY + 12, { align: "center" });

	// Salvar o arquivo
	doc.save(`Fechamento-Caixa-${dataFechamento.numero_caixa}-Sessao-${dataFechamento.id_sessao}.pdf`);
}
